import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone, MessageCircle, Clock, Shield, Palette, Droplets, ChevronDown, Award, Star, Heart, Quote } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';



const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  &:nth-child(even) {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  }

  &:nth-child(odd) {
    background: white;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${brandTheme.colors.primary[900]};
  line-height: 1.2;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.secondary[500]} 100%);
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${brandTheme.colors.neutral.gray[600]};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e5e7eb;

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: white;
  }

  h3 {
    margin-bottom: 1rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
  }
`;

const BenefitsList = styled.div`
  ul {
    list-style: none;
    padding: 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background: #f8fafc;
    border-radius: 10px;
    border-left: 4px solid #FECE1B;

    svg {
      color: #10b981;
      flex-shrink: 0;
    }
  }
`;

const ProcessSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;

  .step-number {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2rem;
    margin: 0 auto 1rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
    font-size: 0.9rem;
  }
`;

const PricingSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const PricingCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;

  &.featured {
    border-color: #FECE1B;
    transform: scale(1.05);
    box-shadow: 0 20px 40px rgba(254, 206, 27, 0.2);
  }

  h3 {
    color: #FECE1B;
    margin-bottom: 1rem;
  }

  .price {
    font-size: 2.5rem;
    font-weight: bold;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
  }

  .price-note {
    color: #666;
    margin-bottom: 2rem;
  }

  ul {
    list-style: none;
    text-align: left;
    margin-bottom: 2rem;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;

      svg {
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  .question {
    padding: 1.5rem 2rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[900]};
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    svg {
      transition: transform 0.3s ease;
      color: ${brandTheme.colors.primary[500]};
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 2rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    color: ${brandTheme.colors.neutral.gray[700]};
    line-height: 1.6;

    &.active {
      padding: 0 2rem 1.5rem 2rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem 1.5rem;
      font-size: 1rem;
    }

    .answer {
      &.active {
        padding: 0 1.5rem 1.25rem 1.5rem;
      }
    }
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const DrivewaysPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: 'How long does a resin driveway installation take?',
      answer: 'Most residential resin driveways are completed within 1-2 days. The exact timeframe depends on the size of the area, weather conditions, and any additional preparation work required. We provide a detailed timeline during your free survey.'
    },
    {
      question: 'Are resin driveways SUDS compliant?',
      answer: 'Yes, our resin bound driveways are fully SUDS (Sustainable Urban Drainage Systems) compliant. The permeable surface allows water to drain naturally, preventing flooding and meeting local planning requirements.'
    },
    {
      question: 'What maintenance does a resin driveway require?',
      answer: 'Resin driveways are very low maintenance. Regular cleaning with a pressure washer or garden hose is usually sufficient. We recommend annual inspection and occasional re-sealing every 3-5 years to maintain optimal performance.'
    },
    {
      question: 'Can resin driveways be installed in winter?',
      answer: 'Yes, resin bound surfacing can be installed in most weather conditions, including winter months. However, we avoid installation during heavy rain, snow, or when temperatures drop below 5°C to ensure optimal curing.'
    },
    {
      question: 'How much does a resin driveway cost?',
      answer: 'Resin driveway costs typically range from £45-65 per square metre, depending on the size, complexity, and chosen materials. We provide free, no-obligation quotes with detailed breakdowns of all costs involved.'
    },
    {
      question: 'Do you offer a warranty on resin driveways?',
      answer: 'Yes, we provide a comprehensive 10+ year warranty on all our resin driveway installations. This covers both materials and workmanship, giving you complete peace of mind with your investment.'
    }
  ];

  const features = [
    {
      icon: <Droplets size={30} />,
      title: 'Permeable Surface',
      description: 'SUDS compliant drainage system prevents flooding and puddles'
    },
    {
      icon: <Shield size={30} />,
      title: '10+ Year Warranty',
      description: 'Comprehensive warranty on materials and workmanship'
    },
    {
      icon: <Clock size={30} />,
      title: 'Quick Installation',
      description: 'Most driveways completed in 1-2 days'
    },
    {
      icon: <Palette size={30} />,
      title: 'Custom Colours',
      description: 'Wide range of colours to match your property'
    }
  ];

  const benefits = [
    'Increases property value by up to 10%',
    'Low maintenance - just occasional cleaning',
    'Slip-resistant surface for safety',
    'Resistant to oil stains and weathering',
    'No weeds or moss growth',
    'Suitable for all weather conditions',
    'Environmentally friendly materials',
    'Professional installation guaranteed'
  ];

  const processSteps = [
    {
      number: 1,
      title: 'Free Survey',
      description: 'We visit your property to assess the area and discuss your requirements'
    },
    {
      number: 2,
      title: 'Site Preparation',
      description: 'Remove existing surface and prepare the base for resin application'
    },
    {
      number: 3,
      title: 'Resin Application',
      description: 'Apply premium resin bound mixture with your chosen aggregate'
    },
    {
      number: 4,
      title: 'Final Inspection',
      description: 'Quality check and handover with maintenance instructions'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resin Driveways London | Premium Resin Bound Driveway Installation</title>
        <meta name="description" content="Expert resin bound driveway installation in London, Surrey & Hertfordshire. Permeable, durable, 10+ year warranty. Free quotes, quick installation. Transform your driveway today!" />
        <meta name="keywords" content="resin driveways London, resin bound driveways London, resin driveway installers London, permeable driveways London, resin surfacing London, resin driveways Westminster, resin bound driveways Camden, resin driveways Islington, resin bound driveways Hackney, resin driveways Tower Hamlets, resin bound driveways Greenwich, resin driveways Lewisham, resin bound driveways Southwark, resin driveways Lambeth, resin bound driveways Wandsworth, resin driveways Hammersmith, resin bound driveways Kensington, resin driveways Chelsea, resin bound driveways Fulham, resin driveways Richmond, resin bound driveways Kingston, resin driveways Merton, resin bound driveways Sutton, resin driveways Croydon, resin bound driveways Bromley, resin driveways Bexley, resin bound driveways Havering, resin driveways Redbridge, resin bound driveways Waltham Forest, resin driveways Enfield, resin bound driveways Barnet, resin driveways Harrow, resin bound driveways Hillingdon, resin driveways Ealing, resin bound driveways Hounslow, resin bound driveways Brent, resin driveways Haringey, resin bound driveways Newham, resin driveways Barking, resin bound driveways Dagenham, resin driveway contractors London, resin surfacing specialists London, resin bound installation London, SUDS compliant driveways London, anti-slip resin driveways London, resin driveway design London, commercial resin driveways London, residential resin driveways London, resin driveway cost London, resin driveway quotes London, best resin driveway company London, resin driveway near me London" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/resin-driveways-london/" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Bound Driveway Installation",
            "description": "Premium resin bound driveway installation service in London",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Resin Surfaces London",
              "url": "https://www.resinsurfaceslondon.co.uk"
            },
            "areaServed": ["London", "Surrey", "Hertfordshire"],
            "serviceType": "Resin Bound Driveway Installation"
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Premium Resin Driveways London"
        subtitle="Transform your driveway with our expert resin bound surfacing. Permeable, durable, and beautiful with 10+ year warranty."
        badge="London's #1 Resin Driveway Specialists"
        backgroundImage="/images/resin-surfaces-bound-work7.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <TwoColumnGrid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/images/resin-surfaces-bound-work7.jpeg" alt="Resin bound driveway London" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Resin Bound Driveways?</h2>
              <p>
                Resin bound driveways are the perfect solution for modern homes. Our permeable resin bound 
                surfacing allows water to drain naturally, preventing flooding and complying with SUDS regulations.
              </p>
              <p>
                Unlike traditional driveways, resin bound surfaces are low maintenance, durable, and can 
                increase your property value. With over 10 years of experience, we've transformed thousands 
                of driveways across London, Surrey, and Hertfordshire.
              </p>
              <BenefitsList>
                <ul>
                  {benefits.slice(0, 4).map((benefit, index) => (
                    <li key={index}>
                      <CheckCircle size={20} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </BenefitsList>
            </motion.div>
          </TwoColumnGrid>

          <SectionTitle>Key Features & Benefits</SectionTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Complete Benefits List</h2>
            <BenefitsList>
              <ul>
                {benefits.map((benefit, index) => (
                  <li key={index}>
                    <CheckCircle size={20} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </BenefitsList>
          </motion.div>
        </Container>
      </Section>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Installation Process</SectionTitle>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <PricingSection>
        <Container>
          <SectionTitle>Investment & Pricing</SectionTitle>
          <PricingCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="featured"
          >
            <h3>Standard Resin Driveway</h3>
            <div className="price">£45-65</div>
            <div className="price-note">per square metre</div>
            <ul>
              <li><CheckCircle size={16} /> Site survey & preparation</li>
              <li><CheckCircle size={16} /> Premium resin bound mixture</li>
              <li><CheckCircle size={16} /> Permeable drainage system</li>
              <li><CheckCircle size={16} /> 10+ year warranty</li>
              <li><CheckCircle size={16} /> Professional installation</li>
              <li><CheckCircle size={16} /> Clean-up & handover</li>
            </ul>
            <StyledButton to="/contact/" className="primary">
              Get Your Quote <ArrowRight size={16} />
            </StyledButton>
          </PricingCard>
        </Container>
      </PricingSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Resin Driveways</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about our resin driveway installation, maintenance, and warranty
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready for Your New Driveway?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
            Join thousands of satisfied customers across London, Surrey & Hertfordshire
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <StyledButton to="/contact/" className="primary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </div>
        </Container>
      </CTASection>
    </>
  );
};

export default DrivewaysPage;
